import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';

import { FormActions } from '../form_layout';
import '../form_layout/form_layout.css';
import './pay_time_warning.css';
import {
  ACK_NOTE_MAX,
  DETAIL_EFFECT,
  DETAIL_TIME,
  ack_note_error,
  build_pay_time_ack,
  pay_time_view
} from './pay_time_warning_rules';

// The in-dialog step shown instead of the form when the server warns about a pay-affecting time
// (A-52): Ops acknowledge with a note and save, or go back and change the value.
export default function PayTimeWarningStep({
  warning,
  detail,
  busy,
  changeLabel,
  actions,
  onAcknowledge,
  onChange
}) {
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  const noteId = useId();
  const titleId = useId();
  const view = pay_time_view(warning, detail);
  const error = touched ? ack_note_error(note) : '';
  // Teacher Payments dialogs use their own field classes; elsewhere the shared pop-up form ones.
  const tp = actions === 'tp';
  const cls = tp
    ? { field: 'tp-field', label: '', input: 'tp-textarea', help: 'tp-hint', error: 'tp-err' }
    : {
        field: 'myt-field',
        label: 'myt-form-label',
        input: 'required-reason-input',
        help: 'myt-form-help',
        error: 'myt-form-error'
      };

  const acknowledge = () => {
    setTouched(true);
    if (busy || ack_note_error(note)) return;
    onAcknowledge(build_pay_time_ack(warning.warnings, note));
  };

  return (
    <section className="pt-warning" aria-labelledby={titleId} data-testid="pay-time-warning">
      <h5 id={titleId} className="pt-warning-title">
        {view.title}
      </h5>
      <div className="pt-warning-box" role="alert">
        <ul className="pt-warning-lines">
          {view.lines.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
        {view.facts.length ? (
          <dl className="pt-warning-facts">
            {view.facts.map((f) => (
              <React.Fragment key={f.key}>
                <dt>{f.label}</dt>
                <dd>{f.text}</dd>
              </React.Fragment>
            ))}
          </dl>
        ) : null}
        {view.message ? <p className="pt-warning-message">{view.message}</p> : null}
        <p className="pt-warning-help">
          Saving records your acknowledgement and note, and flags the change for review.
        </p>
      </div>
      <div className={cls.field}>
        <label htmlFor={noteId} className={cls.label || undefined}>
          Why is this right? (required)
        </label>
        <textarea
          id={noteId}
          className={`${cls.input} pt-warning-note`}
          value={note}
          maxLength={ACK_NOTE_MAX}
          aria-required="true"
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? `${noteId}-error` : undefined}
          placeholder="e.g. The class was moved on the teacher's request made yesterday"
          onChange={(e) => setNote(e.target.value)}
        />
        <p className={cls.help}>
          {note.trim().length}/{ACK_NOTE_MAX}
        </p>
        {error ? (
          <p id={`${noteId}-error`} className={cls.error}>
            * {error}
          </p>
        ) : null}
      </div>
      {tp ? (
        <div className="tp-modal-ft">
          {/* aria-disabled, not disabled: disabling the focused button drops focus (TP guardrail). */}
          <button
            type="button"
            className="tp-btn-ghost"
            aria-disabled={busy ? 'true' : undefined}
            onClick={busy ? undefined : onChange}
          >
            {changeLabel}
          </button>
          <button
            type="button"
            className="tp-btn"
            aria-disabled={busy ? 'true' : undefined}
            onClick={acknowledge}
          >
            {busy ? 'Saving…' : 'Acknowledge and save'}
          </button>
        </div>
      ) : (
        <FormActions>
          <button type="button" className="button-secondary" disabled={busy} onClick={onChange}>
            {changeLabel}
          </button>
          <button type="button" disabled={busy} onClick={acknowledge}>
            {busy ? 'Saving…' : 'Acknowledge and save'}
          </button>
        </FormActions>
      )}
    </section>
  );
}

PayTimeWarningStep.propTypes = {
  warning: PropTypes.shape({
    warnings: PropTypes.arrayOf(PropTypes.string).isRequired,
    facts: PropTypes.object,
    message: PropTypes.string
  }).isRequired,
  // 'time' (session/student dialogs: time facts only) or 'effect' (Teacher Payments screens)
  detail: PropTypes.oneOf([DETAIL_TIME, DETAIL_EFFECT]),
  busy: PropTypes.bool,
  changeLabel: PropTypes.string,
  // 'tp': Teacher Payments dialog buttons (tp-btn); default: the shared pop-up form buttons
  actions: PropTypes.oneOf(['form', 'tp']),
  onAcknowledge: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired
};

PayTimeWarningStep.defaultProps = {
  detail: DETAIL_TIME,
  busy: false,
  changeLabel: 'Change',
  actions: 'form'
};
